import { get } from 'lodash-es';
import { Checkbox, Radio, Select, Tooltip } from '@easyv/react-components';
import { Component, RuntimeScene, VirtualChildComponent } from '@easytwin/runtime';
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { UIPrimitive } from '@/type';
import { Gizmo, ToggleStringIcon } from '@/baseui';
import { ImmerFormItem } from './ImmerFormItem';
import { ProButton } from './ProButton';
import { ProColorsPicker } from './ProColorsPicker';
import { FileUpload } from './FileUpload';
import { TextureSelection } from './TextureSelection';
import { TextStyleGroup } from './TextStyleGroup';
import { ProInputPercentNumber } from './ProInputPercentNumber';
import { ProCheckbox } from './ProCheckbox';
import { GridMatrixWithInput } from './GridMatrix';
import { ProInputEulerSlider } from './ProInputEulerSlider';
import { ProInputSliderForTime } from './ProInputSliderForTime';
import {
  CollapseBox,
  EntitySelect,
  ParticleSystemBaseInput,
  ProInput,
  ProInputEulerNumber,
  ProInputNumber,
  ProInputSlider,
  Tabs,
  Vector2FormItem,
  Vector3FormItem,
} from '.';
import classNames from 'classnames';

interface PrimitiveProps {
  comp: Component | VirtualChildComponent | RuntimeScene;
  target: any;
  disabled?: boolean;
  schema: UIPrimitive;
  onChange: (name: string, value: unknown) => void;
  onConfirm: (name: string, value: unknown) => void;
}

export const Primitive: React.FC<PrimitiveProps> = observer((props) => {
  const { target, disabled, schema, comp, onChange, onConfirm } = props;

  if (!schema || target === undefined) return null;
  if (schema.hidden) return null;

  switch (schema.uiType) {
    case 'InputNumber':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          {schema.percent ? (
            <ProInputPercentNumber
              name={schema.fieldName}
              value={get(target, schema.fieldName)}
              size='small'
              disabled={disabled || schema.disabled}
              min={schema.min}
              max={schema.max}
              prefix={schema.prefix}
              suffix={schema.suffix}
              precisionOnShow={schema.precisionOnShow}
              precisionOnSave={schema.precisionOnSave}
              onChange={onChange}
              onBlur={onConfirm}
              onPointMoveStart={onChange}
              onPointMoveEnd={onConfirm}
            />
          ) : (
            <ProInputNumber
              name={schema.fieldName}
              value={get(target, schema.fieldName)}
              size='small'
              disabled={disabled || schema.disabled}
              min={schema.min}
              max={schema.max}
              prefix={schema.prefix}
              suffix={schema.suffix}
              precisionOnShow={schema.precisionOnShow}
              precisionOnSave={schema.precisionOnSave}
              onChange={onChange}
              onBlur={onConfirm}
            />
          )}
        </ImmerFormItem>
      );

    case 'InputEulerNumber':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProInputEulerNumber
            name={schema.fieldName}
            value={get(target, schema.fieldName)}
            size='small'
            disabled={disabled || schema.disabled}
            min={schema.min}
            max={schema.max}
            prefix={schema.prefix}
            suffix={schema.suffix}
            precisionOnShow={schema.precisionOnShow}
            precisionOnSave={schema.precisionOnSave}
            onChange={onChange}
            onBlur={onConfirm}
          />
        </ImmerFormItem>
      );

    case 'InputNumberGroup':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <div className='flex items-center w-full gap-x-2'>
            {schema.inputNumbers.map((s) =>
              s.percent ? (
                <ProInputPercentNumber
                  key={s.fieldName}
                  name={s.fieldName}
                  value={get(target, s.fieldName)}
                  size='small'
                  disabled={disabled || schema.disabled}
                  min={s.min}
                  max={s.max}
                  prefix={s.prefix}
                  suffix={s.suffix}
                  precisionOnShow={s.precisionOnShow}
                  precisionOnSave={s.precisionOnSave}
                  onChange={onChange}
                  onBlur={onConfirm}
                  onPointMoveStart={onChange}
                  onPointMoveEnd={onConfirm}
                />
              ) : (
                <ProInputNumber
                  key={s.fieldName}
                  name={s.fieldName}
                  value={get(target, s.fieldName)}
                  size='small'
                  disabled={disabled || schema.disabled}
                  min={s.min}
                  max={s.max}
                  prefix={s.prefix}
                  suffix={s.suffix}
                  precisionOnShow={s.precisionOnShow}
                  precisionOnSave={s.precisionOnSave}
                  onChange={onChange}
                  onBlur={onConfirm}
                />
              ),
            )}
          </div>
        </ImmerFormItem>
      );

    case 'InputSlider':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProInputSlider
            className={schema.className}
            name={schema.fieldName}
            value={get(target, schema.fieldName)}
            disabled={disabled || schema.disabled}
            prefix={schema.prefix}
            suffix={schema.suffix}
            min={schema.min}
            max={schema.max}
            step={schema.step}
            precisionOnShow={schema.precisionOnShow}
            precisionOnSave={schema.precisionOnSave}
            limitSliderOnly={schema.limitSliderOnly}
            showInput={schema.showInput}
            onChange={onChange}
            onBlur={onConfirm}
          />
        </ImmerFormItem>
      );

    case 'InputEulerSlider':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProInputEulerSlider
            name={schema.fieldName}
            value={get(target, schema.fieldName)}
            disabled={disabled || schema.disabled}
            prefix={schema.prefix}
            suffix={schema.suffix}
            min={schema.min}
            max={schema.max}
            step={schema.step}
            precisionOnShow={schema.precisionOnShow}
            precisionOnSave={schema.precisionOnSave}
            limitSliderOnly={schema.limitSliderOnly}
            showInput={schema.showInput}
            onChange={onChange}
            onBlur={onConfirm}
          />
        </ImmerFormItem>
      );

    case 'InputSliderForTime':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProInputSliderForTime
            className={schema.className}
            name={schema.fieldName}
            value={get(target, schema.fieldName)}
            disabled={disabled || schema.disabled}
            prefix={schema.prefix}
            suffix={schema.suffix}
            min={schema.min}
            max={schema.max}
            step={schema.step}
            precisionOnShow={schema.precisionOnShow}
            precisionOnSave={schema.precisionOnSave}
            limitSliderOnly={schema.limitSliderOnly}
            showInput={schema.showInput}
            onChange={onChange}
            onBlur={onConfirm}
          />
        </ImmerFormItem>
      );

    case 'InputVector2':
      return (
        <Vector2FormItem
          disabled={disabled || schema.disabled}
          name={schema.fieldName}
          label={schema.label}
          value={get(target, schema.fieldName)}
          onChange={onChange}
          onBlur={onConfirm}
          prefix={schema.prefix}
          suffix={schema.suffix}
          constrainable={schema.constrainable}
          tooltip={schema.tooltip}
          precisionOnShow={schema.precisionOnShow}
          precisionOnSave={schema.precisionOnSave}
        />
      );

    case 'InputVector3':
      return (
        <Vector3FormItem
          disabled={disabled || schema.disabled}
          name={schema.fieldName}
          label={schema.label}
          useEuler={schema.useEuler}
          value={get(target, schema.fieldName)}
          onChange={onChange}
          onBlur={onConfirm}
          prefix={schema.prefix}
          suffix={schema.suffix}
          constrainable={schema.constrainable}
          tooltip={schema.tooltip}
          hideY={schema.hideY}
          hideZ={schema.hideZ}
          hideX={schema.hideX}
          precisionOnShow={schema.precisionOnShow}
          precisionOnSave={schema.precisionOnSave}
        />
      );

    case 'InputText':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProInput
            name={schema.fieldName}
            maxLength={schema.maxLength}
            value={get(target, schema.fieldName)}
            size='small'
            disabled={disabled || schema.disabled}
            prefix={schema.prefix}
            suffix={schema.suffix}
            onChange={onChange}
            onBlur={onConfirm}
          />
        </ImmerFormItem>
      );

    case 'Select': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <Select
            size='small'
            disabled={disabled || schema.disabled}
            value={get(target, schema.fieldName)}
            onChange={(value) => {
              onConfirm(schema.fieldName, value);
            }}
            allowClear={schema.allowClear}
          >
            {schema.options?.map((a) => (
              <Select.Option key={a.key} value={a.key}>
                {a.img && (
                  <img
                    src={a.img}
                    className='w-3 h-3 mr-2 bg-gray-200 rounded-sm inline-block align-middle'
                  ></img>
                )}
                <span>{a.label}</span>
              </Select.Option>
            ))}
          </Select>
        </ImmerFormItem>
      );
    }

    case 'ColorPicker':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProColorsPicker
            value={get(target, schema.fieldName)}
            disabledAlpha={schema.disableAlpha}
            disabledOutAlpha={schema.disableOutputAlpha}
            type={schema.type}
            onChange={(value) => {
              onChange(schema.fieldName, value);
            }}
            onChangeComplete={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          />
        </ImmerFormItem>
      );
    case 'CheckBox':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProCheckbox
            disabled={disabled || schema.disabled}
            checked={get(target, schema.fieldName)}
            overrideValue={schema.overrideValue}
            onChange={(value: any) => {
              onConfirm(schema.fieldName, value);
            }}
          >
            {schema.text}
          </ProCheckbox>
        </ImmerFormItem>
      );

    case 'CheckBoxGroup': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <div className='flex items-center w-full gap-x-2'>
            {schema.checkboxes.map((checkbox) => (
              <span key={checkbox.fieldName} className='flex-1'>
                <Checkbox
                  disabled={disabled || schema.disabled || checkbox.disabled}
                  checked={get(target, checkbox.fieldName)}
                  onChange={(value) => {
                    onConfirm(checkbox.fieldName, value);
                  }}
                />
                <span className={classNames('ms-2', disabled && 'text-content4')}>
                  {checkbox.label}
                </span>
              </span>
            ))}
          </div>
        </ImmerFormItem>
      );
    }

    case 'FileUpload':
      return (
        <ImmerFormItem
          labelVerticalAlign='top'
          size='small'
          label={schema.label}
          tooltip={schema.tooltip}
        >
          <FileUpload
            className='my-1'
            disabled={disabled || schema.disabled}
            uploadAcceptExt={schema.accept}
            maxFileSize={schema.maxFileSize}
            maxImageSize={schema.maxImageSize}
            maxModelSize={schema.maxModelSize}
            value={get(target, schema.fieldName)}
            onCompleted={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          />
        </ImmerFormItem>
      );

    case 'Button':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <ProButton
            style={schema.style}
            type={schema.type}
            size={schema.size || 'small'}
            long
            disableTab={schema.disableTab}
            disabled={disabled || schema.disabled}
            onClick={schema.onClick}
          >
            {schema.text}
          </ProButton>
        </ImmerFormItem>
      );

    case 'ButtonGroup': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <div className='flex items-center w-full gap-x-2'>
            {schema.buttons.map((btn) => (
              <ProButton
                long
                key={btn.text}
                type={btn.type}
                style={btn.style}
                size={btn.size || 'small'}
                disabled={disabled || schema.disabled || btn.disabled}
                onClick={btn.onClick}
              >
                {btn.text}
              </ProButton>
            ))}
          </div>
        </ImmerFormItem>
      );
    }

    case 'ToggleStringIconGroup': {
      return (
        <ImmerFormItem label={schema.label} disabled={disabled || schema.disabled}>
          <div className='flex items-center gap-x-2'>
            {schema.icons.map((a) => {
              return (
                <Radio.Group
                  key={a.key}
                  className='!flex !gap-x-2'
                  disabled={disabled || schema.disabled}
                  value={get(target, schema.fieldName)}
                  onChange={(value) => {
                    onConfirm(schema.fieldName, value);
                  }}
                >
                  <Radio key={a.key} value={a.key} className='!mr-0 !pl-0'>
                    {({ checked }) => {
                      return (
                        <Tooltip content={a.tooltip}>
                          <span>
                            <Gizmo
                              wrapperClassType='playControl'
                              highlight={checked}
                              icon={a.icon}
                            />
                          </span>
                        </Tooltip>
                      );
                    }}
                  </Radio>
                </Radio.Group>
              );
            })}
          </div>
        </ImmerFormItem>
      );
    }

    case 'Tabs': {
      return (
        <Tabs
          comp={comp}
          allowEmpty={schema.allowEmpty}
          title={schema.title}
          name={schema.fieldName}
          disableFold={schema.disableFold}
          value={get(target, schema.fieldName)}
          useValueName={schema.useValueName}
          childSchema={schema.childSchema}
          onAddItem={schema.onAddItem}
          onChange={onChange}
          onConfirm={onConfirm}
          onTabChange={schema.onTabChange}
          onItemAdd={schema.onItemAdd}
          onItemRemove={schema.onItemRemove}
          selectKey={schema.selectKey}
          setSelectKey={schema.setSelectKey}
        />
      );
    }

    case 'Group': {
      const children = schema.children.map((field, index) => (
        <Primitive
          key={index}
          disabled={disabled || schema.disabled}
          comp={comp}
          target={target}
          schema={field}
          onChange={onChange}
          onConfirm={onConfirm}
        />
      ));

      return (
        <CollapseBox
          type={schema.type}
          title={schema.label}
          titleExtra={schema.titleExtra}
          headerClassName='px-0'
          showConnectionLine={schema.showConnectionLine}
          showDivider={schema.showDivider}
          disable={schema.disableFold}
          showChildren={schema.showChildren}
        >
          {children}
        </CollapseBox>
      );
    }

    case 'PureGroup': {
      const children = schema.children.map((field, index) => (
        <Primitive
          key={index}
          disabled={disabled || schema.disabled}
          comp={comp}
          target={target}
          schema={field}
          onChange={onChange}
          onConfirm={onConfirm}
        />
      ));

      return <div>{children}</div>;
    }

    case 'Custom': {
      return (
        <schema.ui
          comp={comp}
          json={target}
          disabled={disabled || schema.disabled || false}
          onChange={onChange}
          onConfirm={onConfirm}
        />
      );
    }

    case 'TextureSelect': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <TextureSelection
            disabled={disabled || schema.disabled}
            comp={comp}
            onChange={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          />
        </ImmerFormItem>
      );
    }

    case 'Radio': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <Radio
            disabled={disabled || schema.disabled}
            className={schema.className}
            checked={get(target, schema.fieldName)}
            onChange={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          >
            {schema.label}
          </Radio>
        </ImmerFormItem>
      );
    }

    case 'RadioGroup': {
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <Radio.Group
            disabled={disabled || schema.disabled}
            type={schema.type || 'button'}
            size={schema.size || 'small'}
            buttonStyle={schema.buttonStyle || 'solid'}
            block
            value={get(target, schema.fieldName)}
            onChange={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          >
            {schema.values.map((v) => (
              <Radio key={v.value} value={v.value}>
                {v.children || v.label}
              </Radio>
            ))}
          </Radio.Group>
        </ImmerFormItem>
      );
    }

    case 'TextStyle': {
      return (
        <TextStyleGroup
          disabled={disabled || schema.disabled}
          label={schema.label}
          value={get(target, schema.fieldName)}
          tooltip={schema.tooltip}
          name={schema.fieldName}
          onChange={onChange}
          onBlur={onConfirm}
        />
      );
    }

    case 'GridMatrix':
      return (
        <ImmerFormItem label={schema.label} tooltip={schema.tooltip} labelVerticalAlign='top'>
          <GridMatrixWithInput
            pos={get(target, schema.fieldName)}
            onChange={(value) => {
              onChange(schema.fieldName, value);
            }}
            onConfirm={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          />
        </ImmerFormItem>
      );

    case 'ParticleSystemBaseInput':
      return (
        <ParticleSystemBaseInput
          label={schema.label}
          name={schema.fieldName}
          useEuler={schema.useEuler}
          suffix={schema.suffix}
          value={get(target, schema.fieldName)}
          onChange={(value) => {
            onChange(schema.fieldName, value);
          }}
          onConfirm={(value) => {
            onConfirm(schema.fieldName, value);
          }}
        />
      );

    case 'EntitySelect':
      return (
        <ImmerFormItem
          label={schema.label}
          tooltip={schema.tooltip}
          disabled={disabled || schema.disabled}
        >
          <EntitySelect
            scene={schema.scene}
            showSearch={schema.showSearch}
            disabled={disabled || schema.disabled}
            placeHolder={schema.placeHolder}
            value={get(target, schema.fieldName)}
            filter={schema.filter}
            onBlur={(value) => {
              onConfirm(schema.fieldName, value);
            }}
          ></EntitySelect>
        </ImmerFormItem>
      );
  }
});
